import React, { useMemo, useState, useEffect, useRef } from 'react';
import { getProducts, getProductsPage, searchProducts, searchProductsByCategoryId, deleteProduct, addNewProduct, getCategories } from '../firebase/services';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../Context/UserContext';
import { PERMISSIONS } from '../Utils/permissions';
import toast from 'react-hot-toast';
import './Admin.css';

export default function ManageProducts() {
  const PAGE_SIZE = 20;
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [jumpToPage, setJumpToPage] = useState('');
  const pageCursorsRef = useRef([null]);
  const searchDebounceRef = useRef(null);
  const { permissions } = useUser();
  const navigate = useNavigate();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const initialPageLoadedRef = useRef(false);

  useEffect(() => {
    if (initialPageLoadedRef.current) return;
    initialPageLoadedRef.current = true;

    const raw = urlSearchParams.get('page');
    const desired = Math.max(1, Number.parseInt(raw || '1', 10) || 1);
    loadInitial(desired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPageInUrl = (nextPage) => {
    const next = new URLSearchParams(urlSearchParams);
    if (!nextPage || nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setUrlSearchParams(next, { replace: true });
  };

  const loadInitial = async (desiredPage = 1) => {
    setLoading(true);
    try {
      const categoriesData = await getCategories();
      setCategories(categoriesData);

      // Initial page (no search)
      const res = await getProductsPage({ pageSize: PAGE_SIZE, cursor: null });
      setProducts(res.items);
      setHasNext(res.items.length === PAGE_SIZE && !!res.nextCursor);
      pageCursorsRef.current = [null, res.nextCursor].filter(v => v !== undefined);

      if (desiredPage > 1) {
        await prefetchCursorsUpTo(desiredPage);
        const cursor = pageCursorsRef.current[desiredPage - 1] || null;
        const desiredRes = await getProductsPage({ pageSize: PAGE_SIZE, cursor });
        setProducts(desiredRes.items);
        setHasNext(desiredRes.items.length === PAGE_SIZE && !!desiredRes.nextCursor);
        if (!pageCursorsRef.current[desiredPage]) {
          pageCursorsRef.current[desiredPage] = desiredRes.nextCursor;
        }
        setPage(desiredPage);
        setPageInUrl(desiredPage);
      } else {
        setPage(1);
        setPageInUrl(1);
      }
    } catch (error) {
      console.error("Failed to load data", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadPage = async (nextPage) => {
    setLoading(true);
    try {
      const cursor = pageCursorsRef.current[nextPage - 1] || null;
      const res = await getProductsPage({ pageSize: PAGE_SIZE, cursor });
      setProducts(res.items);
      setHasNext(res.items.length === PAGE_SIZE && !!res.nextCursor);

      // Store cursor for the next page if not already stored
      if (!pageCursorsRef.current[nextPage]) {
        pageCursorsRef.current[nextPage] = res.nextCursor;
      }
      setPage(nextPage);
      setPageInUrl(nextPage);
    } catch (error) {
      console.error('Failed to load page', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await deleteProduct(id);
        toast.success("Product deleted successfully");
        // Reload current page (or first page if searching)
        if (searchTerm.trim()) {
          setSearchTerm('');
          await loadInitial();
        } else {
          await loadPage(Math.max(1, page));
        }
      } catch (error) {
        toast.error("Failed to delete product");
      }
    }
  };

  const getProductName = (p) => {
    if (p.nameEn && p.nameAr) return `${p.nameEn} / ${p.nameAr}`;
    if (p.nameEn) return p.nameEn;
    if (p.nameAr) return p.nameAr;
    if (typeof p.name === 'string') return p.name;
    return "Unnamed Product";
  };

  const searchResults = useMemo(() => {
    // In search mode, `products` is already the fetched search results list.
    // In browse mode, `products` is the current page items.
    return products;
  }, [products]);

  const isSearchMode = searchTerm.trim().length > 0;

  // Client-side paginate search results (already limited) to PAGE_SIZE per page
  const searchTotalPages = Math.max(1, Math.ceil(searchResults.length / PAGE_SIZE));
  const pageProducts = useMemo(() => {
    if (!isSearchMode) return products;
    const start = (page - 1) * PAGE_SIZE;
    return searchResults.slice(start, start + PAGE_SIZE);
  }, [isSearchMode, products, searchResults, page]);

  const rangeStart = searchResults.length === 0 ? 0 : ((page - 1) * PAGE_SIZE + 1);
  const rangeEnd = Math.min(page * PAGE_SIZE, searchResults.length);

  const prefetchCursorsUpTo = async (targetPage) => {
    // Cursor array semantics:
    // - To fetch page N, we need pageCursorsRef.current[N - 1]
    // - After fetching page N, we store nextCursor at index N
    // So pageCursorsRef.current.length is the max page we can fetch right now.
    let maxFetchablePage = pageCursorsRef.current.length;
    while (maxFetchablePage < targetPage) {
      const cursor = pageCursorsRef.current[maxFetchablePage - 1] || null;
      const res = await getProductsPage({ pageSize: PAGE_SIZE, cursor });

      // Store cursor for the next page
      if (!pageCursorsRef.current[maxFetchablePage]) {
        pageCursorsRef.current[maxFetchablePage] = res.nextCursor;
      }

      const canContinue = res.items.length === PAGE_SIZE && !!res.nextCursor;
      maxFetchablePage = pageCursorsRef.current.length;

      if (!canContinue) {
        break;
      }
    }
  };

  const handleJump = async () => {
    const raw = jumpToPage.toString().trim();
    if (!raw) return;

    const target = Number(raw);
    if (!Number.isFinite(target) || target < 1) {
      toast.error('Enter a valid page number');
      return;
    }

    if (isSearchMode) {
      const clamped = Math.max(1, Math.min(searchTotalPages, Math.floor(target)));
      setPage(clamped);
      return;
    }

    const wanted = Math.floor(target);
    if (wanted === page) return;

    try {
      setLoading(true);
      await prefetchCursorsUpTo(wanted);

      const canFetchWanted = pageCursorsRef.current.length >= wanted;
      if (!canFetchWanted) {
        toast.error('That page does not exist');
        return;
      }

      await loadPage(wanted);
    } catch (e) {
      console.error('Failed to jump to page', e);
      toast.error('Failed to jump to page');
    } finally {
      setLoading(false);
    }
  };

  const getCategorySearchIds = (q) => {
    const needle = (q || '').toLowerCase();
    if (!needle) return [];
    return categories
      .filter(c => {
        const name = (c?.name || '').toString();
        const nameEn = (c?.nameEn || '').toString();
        const nameAr = (c?.nameAr || '').toString();
        const combined = `${name} ${nameEn} ${nameAr}`.toLowerCase();
        return combined.includes(needle);
      })
      .map(c => c.id)
      .filter(Boolean);
  };

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const q = searchTerm.trim();

    // Browse mode: reset to paginated list (5 per page)
    if (!q) {
      setPage(1);
      loadPage(1);
      return;
    }

    // Search mode (debounced): query Firestore by keyword + by matching category names
    searchDebounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const categoryIds = getCategorySearchIds(q).slice(0, 3);
        const calls = [searchProducts({ text: q, maxResults: 40 })];
        categoryIds.forEach((id) => calls.push(searchProductsByCategoryId({ categoryId: id, maxResults: 40 })));

        const resultsLists = await Promise.all(calls);
        const merged = [];
        const seen = new Set();
        resultsLists.flat().forEach((p) => {
          if (!p?.id) return;
          if (seen.has(p.id)) return;
          seen.add(p.id);
          merged.push(p);
        });

        setProducts(merged.slice(0, 100));
        setPage(1);
        setHasNext(false);
      } catch (err) {
        console.error('Search failed', err);
        toast.error('Search failed');
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, categories]);

  const getProductImage = (p) => {
    if (p.imageUrls && p.imageUrls.length > 0) return p.imageUrls[0];
    if (p.imageUrl) return p.imageUrl;
    if (p.images && p.images.length > 0) return p.images[0];
    return "https://placehold.co/40x40?text=No+Image";
  };

  const getProductCategories = (p) => {
    if (p.categories && Array.isArray(p.categories)) {
      return p.categories.map(catIdOrName => {
        // Try to find category by ID first
        const cat = categories.find(c => c.id === catIdOrName);
        if (cat) return cat.name;
        
        // If not found by ID, maybe it's already a name (legacy data)
        return catIdOrName;
      }).join(', ');
    }
    return p.category || 'Uncategorized';
  };

  const handleExport = async () => {
    setLoading(true);
    const toastId = 'export-products-csv';
    toast.loading('Preparing export…', { id: toastId });

    try {
      const allProducts = await getProducts();
      if (!allProducts || allProducts.length === 0) {
        toast.error('No products to export', { id: toastId });
        return;
      }

    const headers = [
      "ID", "NameEn", "NameAr", "DescriptionEn", "DescriptionAr", 
      "Price", "Stock", "Categories", "ImageUrls", 
      "IsBestSeller", "IsFeatured", "RequiresPrescription"
    ];

      const csvContent = [
      headers.join(','),
      ...allProducts.map(p => {
        const row = [
          p.id,
          `"${(p.nameEn || '').replace(/"/g, '""')}"`,
          `"${(p.nameAr || '').replace(/"/g, '""')}"`,
          `"${(p.descriptionEn || '').replace(/"/g, '""')}"`,
          `"${(p.descriptionAr || '').replace(/"/g, '""')}"`,
          p.price || 0,
          p.stock || 0,
          `"${(p.categories || []).map(catIdOrName => {
            const cat = categories.find(c => c.id === catIdOrName);
            return cat ? cat.name : catIdOrName;
          }).join(';')}"`,
          `"${(p.imageUrls || []).join(';')}"`,
          p.isBestSeller || false,
          p.isFeatured || false,
          p.requiresPrescription || false
        ];
        return row.join(',');
      })
    ].join('\n');

    downloadCSV(csvContent, 'products_export.csv');
      toast.success(`Exported ${allProducts.length} products`, { id: toastId });
    } catch (error) {
      console.error('Failed to export products', error);
      toast.error('Failed to export products', { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSample = () => {
    const headers = [
      "NameEn", "NameAr", "DescriptionEn", "DescriptionAr", 
      "Price", "Stock", "Categories", "ImageUrls", 
      "IsBestSeller", "IsFeatured", "RequiresPrescription"
    ];
    
    const sampleRow = [
      "Sample Product", "منتج تجريبي", "This is a sample description.", "هذا وصف تجريبي.",
      "100.00", "50", "Food;Dogs", "https://example.com/image1.jpg;https://example.com/image2.jpg",
      "false", "true", "false"
    ];

    const csvContent = [headers.join(','), sampleRow.join(',')].join('\n');
    downloadCSV(csvContent, 'sample_product_import.csv');
  };

  const downloadCSV = (content, filename) => {
    const blob = new Blob(["\uFEFF" + content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      const text = evt.target.result;
      const lines = text.split('\n');
      // Simple CSV parser (doesn't handle newlines inside quotes perfectly, but good enough for simple data)
      // Assuming header is first line
      const headers = lines[0].split(',').map(h => h.trim());
      
      let successCount = 0;
      let errorCount = 0;

      setLoading(true);
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        
        try {
          // Handle quoted strings with commas
          const row = [];
          let inQuote = false;
          let currentVal = '';
          for (let char of lines[i]) {
             if (char === '"') {
                inQuote = !inQuote;
             } else if (char === ',' && !inQuote) {
                row.push(currentVal.trim().replace(/^"|"$/g, '').replace(/""/g, '"'));
                currentVal = '';
             } else {
                currentVal += char;
             }
          }
          row.push(currentVal.trim().replace(/^"|"$/g, '').replace(/""/g, '"'));

          const data = {};
          headers.forEach((h, idx) => data[h] = row[idx]);

          // Map CSV fields to formData structure
          const productData = {
            nameEn: data.NameEn || '',
            nameAr: data.NameAr || '',
            name: (data.NameEn || '') + ' / ' + (data.NameAr || ''),
            descriptionEn: data.DescriptionEn || '',
            descriptionAr: data.DescriptionAr || '',
            description: (data.DescriptionEn || '') + '\n' + (data.DescriptionAr || ''),
            price: data.Price || 0,
            stock: data.Stock || 0,
            categories: data.Categories ? data.Categories.split(';').map(c => c.trim()) : [],
            imageUrls: data.ImageUrls ? data.ImageUrls.split(';').map(u => u.trim()) : [],
            isBestSeller: data.IsBestSeller === 'true',
            isFeatured: data.IsFeatured === 'true',
            requiresPrescription: data.RequiresPrescription === 'true'
          };

          await addNewProduct(productData, []); // Pass empty array for files, use imageUrls in data
          successCount++;
        } catch (err) {
          console.error("Error importing row " + i, err);
          errorCount++;
        }
      }
      setLoading(false);
      toast.success(`Imported ${successCount} products. ${errorCount} failed.`);
      await loadInitial();
      e.target.value = ''; // Reset file input
    };
    reader.readAsText(file);
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div>
            <h2>Products</h2>
            <p>Manage your product inventory</p>
        </div>
        <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
            <button onClick={handleDownloadSample} className="btn-secondary">
              <ion-icon name="document-text-outline"></ion-icon> Sample CSV
            </button>
            <label className="btn-secondary" style={{cursor: 'pointer'}}>
              <ion-icon name="cloud-upload-outline"></ion-icon> Import CSV
              <input type="file" accept=".csv" onChange={handleImport} style={{display: 'none'}} />
            </label>
            <button onClick={handleExport} className="btn-secondary">
              <ion-icon name="cloud-download-outline"></ion-icon> Export CSV
            </button>
            {permissions?.[PERMISSIONS.PRODUCTS_MANAGE] && (
                <Link to="/admin/add-product" className="submit-btn" style={{textDecoration: 'none'}}>
                    <ion-icon name="add-outline"></ion-icon> Add Product
                </Link>
            )}
        </div>
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        flexWrap: 'wrap',
        marginBottom: '12px'
      }}>
        <div style={{ flex: 1, minWidth: '260px' }}>
          <input
            className="custom-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name or ID..."
            aria-label="Search products"
          />
        </div>
        <div style={{ color: '#6d7175', fontSize: '0.9rem' }}>
          {loading
            ? 'Loading…'
            : (isSearchMode
              ? `Showing ${rangeStart}-${rangeEnd} of ${searchResults.length}`
              : `Showing ${products.length} (page ${page})`)}
        </div>
      </div>

      <div className="admin-card" style={{padding: 0, overflow: 'hidden'}}>
        {loading ? (
            <div style={{padding: '40px', textAlign: 'center'}}>Loading products...</div>
        ) : (
            <div className="table-container" style={{border: 'none'}}>
            <table className="admin-table">
                <thead>
                    <tr>
                        <th>Product</th>
                        <th>Status</th>
                        <th>Stock</th>
                        <th>Price</th>
                        <th>Categories</th>
                        <th style={{textAlign: 'right'}}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                  {pageProducts.map(product => (
                        <tr key={product.id}>
                            <td style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                                <img 
                                    src={getProductImage(product)} 
                                    alt={getProductName(product)} 
                                    style={{width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #e5e7eb'}} 
                                    onError={(e) => {e.target.src = 'https://placehold.co/40x40?text=Error'}}
                                />
                                <div style={{display: 'flex', flexDirection: 'column'}}>
                                    <span style={{fontWeight: 500, color: '#111827'}}>{getProductName(product)}</span>
                                </div>
                            </td>
                            <td>
                                <span style={{
                                    padding: '4px 8px', 
                                    borderRadius: '12px', 
                                    fontSize: '0.75rem', 
                                    fontWeight: 600,
                                    backgroundColor: (Number(product.stock) || 0) > 0 ? '#def7ec' : '#fde8e8',
                                    color: (Number(product.stock) || 0) > 0 ? '#03543f' : '#9b1c1c'
                                }}>
                                    {(Number(product.stock) || 0) > 0 ? 'Active' : 'Out of Stock'}
                                </span>
                            </td>
                            <td>
                                {product.stock}
                            </td>
                            <td>
                                {product.price} EGP
                            </td>
                            <td style={{color: '#6b7280', textTransform: 'capitalize'}}>
                                {getProductCategories(product)}
                            </td>
                            <td style={{textAlign: 'right'}}>
                                <button 
                                  onClick={() => navigate(`/admin/edit-product/${product.id}?returnPage=${page}`)}
                                    className="action-btn edit"
                                    title="Edit"
                                >
                                    <ion-icon name="create-outline"></ion-icon>
                                </button>
                                {permissions?.[PERMISSIONS.PRODUCTS_MANAGE] && (
                                    <button 
                                        onClick={() => handleDelete(product.id)}
                                        className="action-btn delete"
                                        title="Delete"
                                    >
                                        <ion-icon name="trash-outline"></ion-icon>
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                    {searchResults.length === 0 && (
                        <tr>
                            <td colSpan={6} style={{padding: '40px', textAlign: 'center', color: '#6b7280'}}>
                                No products found.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {searchResults.length > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
                borderTop: '1px solid #e1e3e5',
                background: '#fff'
              }}>
                <button
                  className="btn-secondary"
                  onClick={() => {
                    if (page <= 1) return;
                    if (isSearchMode) setPage(p => Math.max(1, p - 1));
                    else loadPage(page - 1);
                  }}
                  disabled={page <= 1 || loading}
                  style={{ opacity: page <= 1 ? 0.5 : 1, cursor: page <= 1 ? 'not-allowed' : 'pointer' }}
                >
                  Prev
                </button>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  flexWrap: 'wrap',
                  justifyContent: 'center'
                }}>
                  <div style={{ color: '#6d7175', fontSize: '0.9rem' }}>
                    {isSearchMode ? `Page ${page} of ${searchTotalPages}` : `Page ${page}`}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#6d7175', fontSize: '0.85rem' }}>Jump to</span>
                    <input
                      className="custom-input"
                      type="number"
                      min={1}
                      max={isSearchMode ? searchTotalPages : undefined}
                      value={jumpToPage}
                      onChange={(e) => setJumpToPage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleJump();
                      }}
                      placeholder={isSearchMode ? `1-${searchTotalPages}` : 'Page'}
                      aria-label="Jump to page"
                      style={{ width: '110px' }}
                      disabled={loading}
                    />
                    <button
                      className="btn-secondary"
                      onClick={handleJump}
                      disabled={loading || !jumpToPage.toString().trim()}
                      style={{ opacity: (loading || !jumpToPage.toString().trim()) ? 0.5 : 1 }}
                    >
                      Go
                    </button>
                  </div>
                </div>

                <button
                  className="btn-secondary"
                  onClick={() => {
                    if (loading) return;
                    if (isSearchMode) {
                      setPage(p => Math.min(searchTotalPages, p + 1));
                    } else {
                      if (!hasNext) return;
                      loadPage(page + 1);
                    }
                  }}
                  disabled={loading || (isSearchMode ? page >= searchTotalPages : !hasNext)}
                  style={{ opacity: (isSearchMode ? (page >= searchTotalPages) : (!hasNext)) ? 0.5 : 1, cursor: (isSearchMode ? (page >= searchTotalPages) : (!hasNext)) ? 'not-allowed' : 'pointer' }}
                >
                  Next
                </button>
              </div>
            )}
            </div>
        )}
      </div>
    </div>
  );
}
